import { curry, reduce, isNil } from 'ramda';
export var Kind;

(function (Kind) {
  Kind["Ok"] = "RESULT_TAG_OK";
  Kind["TError"] = "RESULT_TAG_ERROR";
  Kind["Pending"] = "PENDING_KIND";
})(Kind || (Kind = {}));

// interned `ok` for undefined values to always return the same object
var undefinedOk = {
  type: Kind.Ok,
  value: undefined
};
/**
 * Constructors
 */

export var ok = function ok(value) {
  if (value === undefined) {
    return undefinedOk;
  } else {
    return {
      type: Kind.Ok,
      value: value
    };
  }
};
export var error = function error(value) {
  return {
    type: Kind.TError,
    value: value
  };
};
export var pending = {
  type: Kind.Pending
};

/**
 * This is intended to be a temporary adapter from older representations of pending data to PendingResult.
 * @param getValue extracts the ok value from the input (e.g. fromValue.entity)
 * @param isPending extracts the pending value from the input (e.g. fromValue.isLoading)
 * @param getError extracts the error value from the input (e.g. fromValue.error). When this returns undefined
 *  we assume there is no error.
 * @returns a PendingResult type that represents the same data.
 */
export var toPendingResult = function toPendingResult(options, fromValue) {
  if (options.isPending(fromValue)) {
    return pending;
  } else if (typeof options.getError(fromValue) !== 'undefined') {
    return error(options.getError(fromValue));
  } else {
    return ok(options.getValue(fromValue));
  }
};
/**
 * Typeguards
 */

export var isOk = function isOk(state) {
  return state.type === Kind.Ok;
};
export var isError = function isError(state) {
  return state.type === Kind.TError;
};
export var isPending = function isPending(state) {
  return state === pending;
};
/**
 * mapResult will map an ok value and short circuit on pending and error values. It returns a new PendingResult with
 * the mapped ok values
 */

export var mapResult = function mapResult(mapper, result) {
  if (isOk(result)) {
    return ok(mapper(result.value));
  }

  return result;
};
/**
 * ap allows you apply pending results of callable functions to other pending results. It will return
 * a new pending result with thisResult's value applied to otherResult's value.
 */

var ap = function ap(thisResult, otherResult) {
  if (isOk(thisResult)) {
    return mapResult(thisResult.value, otherResult);
  }

  return thisResult;
};

function mapAllResults(mapper, results) {
  if (mapper.length !== results.length) {
    throw new Error('Mapper function must have same number of arguments as result array');
  }

  return reduce(ap, ok(curry(mapper)), results);
}

export { mapAllResults };
/**
 * Returns the ok value of a pending result or a default. If the value is not ok and a default is not provided,
 * the default behavior will be followed
 * * Pending state will return undefined
 * * Errored state will throw the error.
 */

export var getOkValue = function getOkValue(pendingResult, defaultValue) {
  if (isOk(pendingResult)) {
    return pendingResult.value;
  } else if (!isNil(defaultValue)) {
    return defaultValue;
  } else if (isPending(pendingResult)) {
    return undefined;
  } else {
    throw new Error("Attempted to retrieve value from error type, ".concat(pendingResult.value));
  }
};